import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@material-ui/core";
import { supabase } from "../../../../supabase";
import { getAvailableCredit } from "../../../../api";
import createClickupTicket from "../../../shared/CreateClickupTicket";

const creditTypes = [
  { id: 1, name: "Unsecured Loan" },
  { id: 2, name: "Advance" },
  { id: 3, name: "Expense paid on behalf of agent" },
];

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onClose
 * @param root0.dealer
 * @param root0.dealer.id
 * @param root0.dealer.name
 * @param root0.dealer.surname
 */
/**
 * CreditModal Component
 *
 * This component renders a modal dialog for creating a new credit application.
 * It allows users to input details such as credit type, cosigner information,
 * and request value, and submit the application.
 *
 * @component
 * @param {object} props - The component props.
 * @param {boolean} props.open - Determines whether the modal is open or closed.
 * @param {Function} props.onClose - Callback function to close the modal.
 * @param {object} props.dealer - Dealer information.
 * @param {string} props.dealer.id - The ID of the dealer.
 * @param {string} props.dealer.first_name - The name of the dealer.
 * @param {string} props.dealer.surname - The surname of the dealer.
 *
 *
 * @example
 * <CreditModal
 *   open={true}
 *   onClose={() => console.log('Modal closed')}
 *   dealer={{ id: '123', name: 'John', surname: 'Doe' }}
 * />
 *
 * @function
 * @name CreditModal
 *
 * @description
 * - Fetches available credit information for the dealer on mount.
 * - Allows users to select credit type, input cosigner details, and request value.
 * - Submits the credit application to the database and creates a ClickUp ticket.
 * - Displays loading state during submission.
 *
 * @dependencies
 * - React hooks: useState, useEffect
 * - External functions: getAvailableCredit, supabase, createClickupTicket
 * - Material-UI components: Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, TextField, Button
 */
export default function CreditModal({
  open,
  onClose,
  dealer: { id, first_name, surname },
}) {
  const [loading, setLoading] = useState(false);
  const [credit, setCredit] = useState([]);
  const [creditType, setCreditType] = useState("Unsecured Loan");
  const [cosigner, setCosigner] = useState(false);
  const [cosignerZcode, setCosignerZcode] = useState("");
  const [requestValue, setRequestValue] = useState("");
  const [cosignor_name, setCosignorName] = useState("");
  const [cosigner_surname, setCosignerSurname] = useState("");

  useEffect(() => {
    const getCredit = async () => {
      const response = await getAvailableCredit({ user_id: id });
      if (response) {
        setCredit(response.data);
      }
    };
    getCredit();
  }, [id]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);

    const { data: dataCredit, error } = await supabase
      .schema("finance")
      .from("credit_applications")
      .insert([
        {
          agent_z_code: id,
          agent_name: first_name,
          agent_surname: surname,
          credit_type: creditType,
          cosignor_z_code: cosignerZcode,
          cosignor_name,
          cosignor_surname: cosigner_surname,
          request_value: requestValue,
          asset_type: null,
          asset_description: null,
          asset_unique_number: null,
          months: credit.Months,
          monthly_payment: credit.MaximumMonthlyPayment,
          max_credit: credit.MaxCredit,
          settled: false,
          outstanding_amount: credit.OutstandingCredit,
        },
      ])
      .select()
      .single();
    if (error) {
      console.error("Error inserting credit application:", error);
      setLoading(false);
      return;
    }

    createClickupTicket(
      `New Credit Application ${id}: ${first_name} ${surname} `,
      "901502149714",
      `New Credit request, click here to generate the contract: https://finance.zawadi.africa/creditContractGenerator?id=${dataCredit.id}`
    );
    onClose();

    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle align="center">New Credit Application</DialogTitle>
      <DialogContent>
        <Typography variant="h6" align="left">
          Available Credit: R {credit.MaxCredit}
        </Typography>
        <Typography variant="h8" align="left">
          Maximum amount of months: {credit.Months}
        </Typography>
        <br />
        <Typography variant="h8" align="left">
          Maximum monthly repayment: R {credit.MaximumMonthlyPayment}
        </Typography>
        <br />
        <Typography variant="h8" align="left">
          Outstanding Credit: R {credit.OutstandingCredit}
        </Typography>
        <br />
        <br />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              label="Select Credit Type"
              fullWidth
              SelectProps={{
                native: true,
              }}
              variant="outlined"
              onChange={e => {
                const selectedType = creditTypes.find(
                  type => type.id === Number(e.target.value)
                );
                if (selectedType) {
                  setCreditType(selectedType.name);
                }
              }}
            >
              <option value="" disabled>
                -- Select an option --
              </option>
              {creditTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Does the application have a cosigner?"
              fullWidth
              SelectProps={{
                native: true,
              }}
              variant="outlined"
              onChange={e => {
                setCosigner(e.target.value === "yes");
                if (e.target.value === "no") {
                  setCosignerZcode("");
                }
              }}
            >
              <option value="" disabled>
                -- Select an option --
              </option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </TextField>
            {cosigner && (
              <>
                <TextField
                  label="Cosigner Z-code"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  onChange={e => {
                    setCosignerZcode(e.target.value);
                  }}
                  value={cosignerZcode}
                />
                <TextField
                  label="Cosignor Name"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  onChange={e => {
                    setCosignorName(e.target.value);
                  }}
                  value={cosignor_name}
                />
                <TextField
                  label="Cosignor Surname"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  onChange={e => {
                    setCosignerSurname(e.target.value);
                  }}
                  value={cosigner_surname}
                />
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Request Value"
              fullWidth
              variant="outlined"
              margin="normal"
              onChange={e => {
                setRequestValue(e.target.value);
              }}
              value={requestValue}
              type="number"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="contained"
          disabled={loading}
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
